import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './Whiteboard.css'; // We'll create this stylesheet

function WhiteBoard({ joinMode = false, sessionId: propSessionId = null, userName = '', isHostProp = true }) {
    const location = useLocation();
    const params = useParams();
    
    // Check for session ID from URL parameters, props, or navigation state
    const urlSessionId = params.sessionId || new URLSearchParams(location.search).get('session');
    const stateSessionId = location.state?.sessionId;
    const finalSessionId = propSessionId || stateSessionId || urlSessionId;
    
    // Check for join mode from props, navigation state, or URL
    const stateJoinMode = location.state?.joinMode;
    const finalJoinMode = joinMode || stateJoinMode || !!urlSessionId;
    
    // Check for user name from props or navigation state
    const stateUserName = location.state?.userName;
    const finalUserName = userName || stateUserName || '';
    
    // Check for host status from props or navigation state
    const stateIsHost = location.state?.isHost;
    const finalIsHost = stateIsHost !== undefined ? stateIsHost : (finalJoinMode ? false : isHostProp);

    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [imgWidth, setImgWidth] = useState(100);
    const [imgHeight, setImgHeight] = useState(100);
    const [imgX, setImgX] = useState(100);
    const [imgY, setImgY] = useState(100);
    const [lineColor, setLineColor] = useState('#1a73e8');
    const [lineWidth, setLineWidth] = useState(5);
    const [tool, setTool] = useState('pen'); // pen, eraser, crop, resize, text
    const [showControls, setShowControls] = useState(true);
    const [lastPoint, setLastPoint] = useState(null);
    const [sessionName, setSessionName] = useState('');
    const [savedSessions, setSavedSessions] = useState([]);
    const [cropStart, setCropStart] = useState(null);
    const [cropEnd, setCropEnd] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [selectedElement, setSelectedElement] = useState(null);
    const [originalDimensions, setOriginalDimensions] = useState(null);
    // Text tool states
    const [isTyping, setIsTyping] = useState(false);
    const [textInputValue, setTextInputValue] = useState('');
    const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
    const [fontFamily, setFontFamily] = useState('Arial');
    const [fontSize, setFontSize] = useState(24);
    const [textInputRef, setTextInputRef] = useState(null);
    // Stylus/touch states
    const [isPressureSensitive, setIsPressureSensitive] = useState(false);
    const [usePressure, setUsePressure] = useState(true);
    const [connectedDevices, setConnectedDevices] = useState([]);
    const [isTabletMode, setIsTabletMode] = useState(false);
    // Remote device states
    const [showRemoteControls, setShowRemoteControls] = useState(false);
    const [remoteSessionId, setRemoteSessionId] = useState(finalSessionId || '');
    const [remoteConnected, setRemoteConnected] = useState(false);
    const [wsConnection, setWsConnection] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [isSharing, setIsSharing] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [connectedUsers, setConnectedUsers] = useState([]);
    const [isHost, setIsHost] = useState(finalIsHost);
    // Add chatbot states
    const [showChatbot, setShowChatbot] = useState(false);
    const [chatMessages, setChatMessages] = useState([
        { sender: 'bot', text: "Hello! I'm your whiteboard assistant. What would you like to draw today?" }
    ]);
    const [chatInput, setChatInput] = useState('');
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    // Add drawing state isolation to prevent interference between users
    const [localDrawing, setLocalDrawing] = useState(false);
    const [remoteDrawingSessions, setRemoteDrawingSessions] = useState(new Map());
    const [lastCanvasUpdate, setLastCanvasUpdate] = useState(Date.now());
    const [pendingUpdates, setPendingUpdates] = useState([]);
    const [isProcessingRemoteUpdate, setIsProcessingRemoteUpdate] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    // Auto-connect when joining via URL or props
    useEffect(() => {
        if (finalJoinMode && finalSessionId) {
            console.log(`Auto-connecting to session: ${finalSessionId} as ${finalIsHost ? 'host' : 'guest'}`);
            setRemoteSessionId(finalSessionId);
            setSessionName(finalUserName || 'Guest');
            setIsHost(finalIsHost);
            
            // Connect immediately
            initializeWebSocket(finalSessionId);
        }
    }, [finalJoinMode, finalSessionId, finalUserName, finalIsHost]);

    // Load saved sessions from localStorage on component mount
    useEffect(() => {
        const loadSavedSessions = () => {
            try {
                const sessions = JSON.parse(localStorage.getItem('whiteboard-sessions') || '[]');
                setSavedSessions(sessions);
            } catch (error) {
                console.error('Error loading saved sessions:', error);
                setSavedSessions([]);
            }
        };
        
        loadSavedSessions();
    }, []);

    // Initialize user ID on component mount
    useEffect(() => {
        if (!currentUserId) {
            const userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
            setCurrentUserId(userId);
            console.log('Generated user ID:', userId);
        }
    }, [currentUserId]);

    useEffect(() => {
        const canvas = canvasRef.current;
        
        // Only proceed if canvas is available
        if (!canvas) return;
        
        // Fix for high-DPI displays
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * 2;
        canvas.height = rect.height * 2;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        const context = canvas.getContext('2d');
        context.scale(2, 2); // Scale for high-DPI
        context.lineCap = "round";
        context.lineJoin = "round"; // Make line joins smooth
        context.strokeStyle = lineColor;
        context.lineWidth = lineWidth;
        // Add smoothing
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        contextRef.current = context;

        // Handle window resize
        const handleResize = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            
            const context = canvas.getContext('2d');
            if (!context) return;
            
            // Save current drawing
            const imageData = context.getImageData(0, 0, canvas.width/2, canvas.height/2);
            
            // Resize canvas
            const newRect = canvas.getBoundingClientRect();
            canvas.width = newRect.width * 2;
            canvas.height = newRect.height * 2;
            canvas.style.width = `${newRect.width}px`;
            canvas.style.height = `${newRect.height}px`;
            
            // Restore context settings
            context.scale(2, 2);
            context.lineCap = "round";
            context.lineJoin = "round";
            context.strokeStyle = lineColor;
            context.lineWidth = lineWidth;
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            
            // Restore drawing
            context.putImageData(imageData, 0, 0);
        };

        window.addEventListener('resize', handleResize);

        // Check for pressure-sensitive devices
        const checkPressureSensitivity = () => {
            if (window.navigator.userAgent.includes('iPad') || 
                window.navigator.userAgent.includes('iPhone') || 
                window.navigator.userAgent.includes('Android') ||
                window.navigator.userAgent.includes('Wacom') ||
                window.navigator.maxTouchPoints > 1) {
                setIsPressureSensitive(true);
            }
            
            // Listen for connected/disconnected devices
            if (navigator.getGamepads) {
                window.addEventListener('gamepadconnected', updateConnectedDevices);
                window.addEventListener('gamepaddisconnected', updateConnectedDevices);
            }
            
            // Listen for device orientation changes to detect tablet mode
            if (window.screen && window.screen.orientation) {
                window.screen.orientation.addEventListener('change', detectTabletMode);
                detectTabletMode();
            }
        };
        
        const updateConnectedDevices = () => {
            const devices = [];
            if (navigator.getGamepads) {
                const gamepads = navigator.getGamepads();
                for (let i = 0; i < gamepads.length; i++) {
                    if (gamepads[i]) {
                        devices.push({
                            id: gamepads[i].id,
                            type: 'gamepad',
                            name: gamepads[i].id
                        });
                    }
                }
            }
            
            // Check for connected Wacom tablets or similar
            if (navigator.usb && navigator.usb.getDevices) {
                navigator.usb.getDevices().then(usbDevices => {
                    usbDevices.forEach(device => {
                        if (device.productName && (
                            device.productName.includes('Tablet') || 
                            device.productName.includes('Pen') || 
                            device.productName.includes('Stylus')
                        )) {
                            devices.push({
                                id: device.productId,
                                type: 'usb',
                                name: device.productName
                            });
                        }
                    });
                    setConnectedDevices(devices);
                });
            } else {
                setConnectedDevices(devices);
            }
        };
        
        const detectTabletMode = () => {
            // Try to detect if device is in tablet mode (touchscreen as primary input)
            const isTablet = window.navigator.maxTouchPoints > 1 && 
                            window.screen.orientation && 
                            window.screen.orientation.type.includes('portrait');
            setIsTabletMode(isTablet);
        };
        
        checkPressureSensitivity();

        if (imgSrc) {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                
                const context = canvas.getContext('2d');
                if (!context) return;
                
                context.drawImage(img, imgX, imgY, imgWidth, imgHeight);
            };
            img.src = imgSrc;
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (navigator.getGamepads) {
                window.removeEventListener('gamepadconnected', updateConnectedDevices);
                window.removeEventListener('gamepaddisconnected', updateConnectedDevices);
            }
            if (window.screen && window.screen.orientation) {
                window.screen.orientation.removeEventListener('change', detectTabletMode);
            }
        };
    }, [imgSrc, imgWidth, imgHeight, imgX, imgY, lineColor, lineWidth]);

    // Helper function for smoother line drawing using quadratic curves
    const drawSmoothLine = (context, p1, p2, pressure = 1) => {
        // Start a new path
        context.beginPath();
        
        // Draw a quadratic curve from last point to current
        context.moveTo(p1.x, p1.y);
        
        // Use the midpoint as control point for smoother curves
        const midPoint = {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2
        };
        
        // Adjust line width based on pressure if enabled
        if (usePressure && pressure !== 1) {
            // Store original line width
            const originalWidth = context.lineWidth;
            // Apply pressure (scale between 50% and 150% of original width)
            context.lineWidth = originalWidth * (0.5 + pressure);
            
            // Draw the line with pressure
            context.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
            context.stroke();
            
            // Restore original line width
            context.lineWidth = originalWidth;
        } else {
            context.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
            context.stroke();
        }
        
        // Return the midpoint for the next segment
        return midPoint;
    };

    const getPointerPos = (canvas, evt) => {
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const pos = {
            x: evt.clientX - rect.left,
            y: evt.clientY - rect.top
        };
        
        // Add pressure if available (e.g., from a stylus)
        if (evt.pressure !== undefined && evt.pressure !== 0) {
            pos.pressure = evt.pressure;
        } else {
            pos.pressure = 1;
        }
        
        return pos;
    };

    const getTouchPos = (canvas, evt) => {
        if (!canvas || !evt.touches || evt.touches.length === 0) return null;
        
        const rect = canvas.getBoundingClientRect();
        const touch = evt.touches[0];
        const pos = {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        };
        
        // Add force/pressure if available (e.g., from 3D Touch or Apple Pencil)
        if (touch.force !== undefined && touch.force !== 0) {
            pos.pressure = touch.force;
        } else {
            pos.pressure = 1;
        }
        
        return pos;
    };

    const getMousePos = (canvas, evt) => {
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return {
            x: evt.clientX - rect.left,
            y: evt.clientY - rect.top,
            pressure: 1 // Mouse doesn't have pressure
        };
    };

    // Crop functionality
    const startCrop = (evt) => {
        if (tool !== 'crop') return;
        
        const canvas = canvasRef.current;
        const point = getMousePos(canvas, evt);
        setCropStart(point);
        setIsCropping(true);
    };

    const updateCrop = (evt) => {
        if (!isCropping || !cropStart) return;
        
        const canvas = canvasRef.current;
        const currentPoint = getMousePos(canvas, evt);
        setCropEnd(currentPoint);
        
        // Redraw canvas to show crop area
        const context = canvas.getContext('2d');
        
        // First, clear any previous overlay
        const savedImageData = context.getImageData(0, 0, canvas.width/2, canvas.height/2);
        
        // Draw semi-transparent overlay
        context.fillStyle = 'rgba(0, 0, 0, 0.5)';
        context.fillRect(0, 0, canvas.width/2, canvas.height/2);
        
        // Clear the crop area
        const width = currentPoint.x - cropStart.x;
        const height = currentPoint.y - cropStart.y;
        context.clearRect(cropStart.x, cropStart.y, width, height);
        
        // Draw border around crop area
        context.strokeStyle = '#ffffff';
        context.lineWidth = 2;
        context.strokeRect(cropStart.x, cropStart.y, width, height);
        
        // Restore original image after a short delay
        setTimeout(() => {
            context.putImageData(savedImageData, 0, 0);
        }, 10);
    };

    const finishCrop = () => {
        if (!isCropping || !cropStart || !cropEnd) return;
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // Calculate crop dimensions
        const cropWidth = Math.abs(cropEnd.x - cropStart.x);
        const cropHeight = Math.abs(cropEnd.y - cropStart.y);
        const cropX = Math.min(cropStart.x, cropEnd.x);
        const cropY = Math.min(cropStart.y, cropEnd.y);
        
        if (cropWidth > 0 && cropHeight > 0) {
            // Get the image data from the crop area
            const imageData = context.getImageData(cropX, cropY, cropWidth, cropHeight);
            
            // Clear the canvas
            context.clearRect(0, 0, canvas.width/2, canvas.height/2);
            
            // Put the cropped image data back on the canvas
            context.putImageData(imageData, 0, 0);
        }
        
        // Reset crop state
        setCropStart(null);
        setCropEnd(null);
        setIsCropping(false);
    };

    // Resize functionality
    const startResize = (evt) => {
        if (tool !== 'resize') return;
        
        const canvas = canvasRef.current;
        const point = getMousePos(canvas, evt);
        
        // Check if user clicked on an image
        if (imgSrc && 
            point.x >= imgX && point.x <= imgX + imgWidth && 
            point.y >= imgY && point.y <= imgY + imgHeight) {
            setSelectedElement('image');
            setOriginalDimensions({ width: imgWidth, height: imgHeight, x: imgX, y: imgY });
            setIsResizing(true);
        }
    };

    const updateResize = (evt) => {
        if (!isResizing || !selectedElement || !originalDimensions) return;
        
        const canvas = canvasRef.current;
        const currentPoint = getMousePos(canvas, evt);
        
        if (selectedElement === 'image') {
            // Calculate new width and height
            const deltaX = currentPoint.x - (originalDimensions.x + originalDimensions.width);
            const deltaY = currentPoint.y - (originalDimensions.y + originalDimensions.height);
            
            const newWidth = Math.max(10, originalDimensions.width + deltaX);
            const newHeight = Math.max(10, originalDimensions.height + deltaY);
            
            setImgWidth(newWidth);
            setImgHeight(newHeight);
            
            // Redraw the image with new dimensions
            const context = canvas.getContext('2d');
            
            if (imgSrc) {
                const img = new Image();
                img.onload = () => {
                    // Clear the area where the image was
                    context.clearRect(originalDimensions.x, originalDimensions.y, originalDimensions.width, originalDimensions.height);
                    // Draw the image with new dimensions
                    context.drawImage(img, originalDimensions.x, originalDimensions.y, newWidth, newHeight);
                };
                img.src = imgSrc;
            }
        }
    };

    const finishResize = () => {
        setIsResizing(false);
        setSelectedElement(null);
        setOriginalDimensions(null);
    };

    // Text functionality
    const startText = (evt) => {
        if (tool !== 'text') return;
        
        // Cancel any existing text input first
        if (isTyping) {
            commitText();
        }
        
        const canvas = canvasRef.current;
        const point = getMousePos(canvas, evt);
        
        setTextPosition({ x: point.x, y: point.y });
        setIsTyping(true);
        setTextInputValue('');
        
        // Delay focus to ensure the element is rendered
        setTimeout(() => {
            if (textInputRef) {
                textInputRef.focus();
            }
        }, 100);
    };

    const handleTextChange = (evt) => {
        setTextInputValue(evt.target.value);
    };

    const handleTextKeyDown = (evt) => {
        // Submit text on Enter key without shift
        if (evt.key === 'Enter' && !evt.shiftKey) {
            commitText();
            evt.preventDefault();
        }
        
        // Cancel on Escape key
        if (evt.key === 'Escape') {
            cancelText();
            evt.preventDefault();
        }
    };

    const handleTextBlur = () => {
        // Use a small delay to allow for other interactions (like clicking a button)
        // before committing the text
        setTimeout(() => {
            if (isTyping) {
                commitText();
            }
        }, 200);
    };

    const commitText = () => {
        if (!isTyping) return;
        
        if (textInputValue.trim()) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            // Configure text styling
            context.globalCompositeOperation = 'source-over';
            context.fillStyle = lineColor;
            context.font = `${fontSize}px ${fontFamily}`;
            context.textBaseline = 'top';
            
            // Split text by newlines to handle multiline text
            const textLines = textInputValue.split('\n');
            
            // Calculate line height based on font size
            const lineHeight = fontSize * 1.2;
            
            // Draw each line of text
            textLines.forEach((line, index) => {
                context.fillText(line, textPosition.x, textPosition.y + (index * lineHeight));
            });
        }
        
        // Reset text state
        setIsTyping(false);
        setTextInputValue('');
    };

    const cancelText = () => {
        setIsTyping(false);
        setTextInputValue('');
    };

    // Modify start drawing to handle different tools
    const startDrawing = (evt) => {
        // Prevent starting if we're processing a remote update
        if (isProcessingRemoteUpdate) {
            return;
        }
        
        // For touch events, convert to pointer position
        let point;
        if (evt.type === 'touchstart') {
            evt.preventDefault(); // Prevent scrolling on touch devices
            point = getTouchPos(canvasRef.current, evt);
            if (!point) return;
        } else if (evt.type === 'pointerdown') {
            point = getPointerPos(canvasRef.current, evt);
        } else {
            point = getMousePos(canvasRef.current, evt);
        }
        
        // Handle different tools
        if (tool === 'crop') {
            startCrop(evt);
            return;
        } else if (tool === 'resize') {
            startResize(evt);
            return;
        } else if (tool === 'text') {
            startText(evt);
            return;
        }
        
        // Configure based on selected tool
        if (tool === 'eraser') {
            contextRef.current.globalCompositeOperation = 'destination-out';
            contextRef.current.lineWidth = lineWidth * 2;
        } else {
            contextRef.current.globalCompositeOperation = 'source-over';
            contextRef.current.strokeStyle = lineColor;
            contextRef.current.lineWidth = lineWidth;
        }
        
        contextRef.current.beginPath();
        contextRef.current.moveTo(point.x, point.y);
        setLastPoint(point);
        setDrawing(true);
        setLocalDrawing(true); // Mark as local drawing session
        
        // Send draw_start event through WebSocket if connected
        if (wsConnection && remoteConnected) {
            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            // Normalize coordinates to 0-1 range based on display size (not internal canvas size)
            const normalizedX = point.x / rect.width;
            const normalizedY = point.y / rect.height;
            
            console.log('Sending start:', { point, rect, normalizedX, normalizedY });
            
            wsConnection.send(JSON.stringify({
                type: 'draw_action',
                action: 'start',
                x: normalizedX,
                y: normalizedY,
                pressure: point.pressure || 1,
                tool: tool,
                color: lineColor,
                width: lineWidth,
                isEraser: tool === 'eraser',
                userId: currentUserId,
                timestamp: Date.now()
            }));
        }
    };

    // Modify finish drawing to handle different tools
    const finishDrawing = (evt) => {
        // Handle different tools
        if (tool === 'crop' && isCropping) {
            finishCrop();
            return;
        } else if (tool === 'resize' && isResizing) {
            finishResize();
            return;
        } else if (tool === 'text') {
            commitText();
            return;
        }
        
        if (drawing && localDrawing) {
        contextRef.current.closePath();
        setDrawing(false);
            setLocalDrawing(false); // End local drawing session
            setLastPoint(null);
            
            // Send draw_end event through WebSocket if connected
            if (wsConnection && remoteConnected) {
                wsConnection.send(JSON.stringify({
                    type: 'draw_action',
                    action: 'end',
                    userId: currentUserId,
                    timestamp: Date.now()
                }));
                
                // After drawing is complete, save the canvas state to sync with others
                // Use a longer delay to ensure the drawing is complete
                setTimeout(() => {
                    if (!localDrawing) { // Only save if we're not drawing again
                        saveCanvasState();
                    }
                }, 500);
            }
        }
    };

    // Modify draw to handle different tools
    const draw = (evt) => {
        // Only process if this is a local drawing session
        if (!drawing || !localDrawing || isProcessingRemoteUpdate) {
            return;
        }
        
        // For touch events, convert to pointer position
        let currentPoint;
        if (evt.type === 'touchmove') {
            evt.preventDefault(); // Prevent scrolling on touch devices
            currentPoint = getTouchPos(canvasRef.current, evt);
            if (!currentPoint) return;
        } else if (evt.type === 'pointermove') {
            currentPoint = getPointerPos(canvasRef.current, evt);
        } else {
            currentPoint = getMousePos(canvasRef.current, evt);
        }
        
        // Handle different tools
        if (tool === 'crop' && isCropping) {
            updateCrop(evt);
            return;
        } else if (tool === 'resize' && isResizing) {
            updateResize(evt);
            return;
        } else if (tool === 'text' && isTyping) {
            // Don't do anything while typing
            return;
        }
        
        if (lastPoint) {
            // Use quadratic curve for smoother lines with pressure sensitivity if available
            const pressure = currentPoint.pressure || 1;
            const midPoint = drawSmoothLine(contextRef.current, lastPoint, currentPoint, pressure);
            setLastPoint(midPoint);
            
            // Send draw_move event through WebSocket if connected (throttled)
            if (wsConnection && remoteConnected) {
                const canvas = canvasRef.current;
                const rect = canvas.getBoundingClientRect();
                // Normalize coordinates to 0-1 range based on display size
                const normalizedX = midPoint.x / rect.width;
                const normalizedY = midPoint.y / rect.height;
                
                // Throttle the sending to reduce network traffic
                const now = Date.now();
                if (now - lastCanvasUpdate > 16) { // ~60fps throttling
                    console.log('Sending move:', { midPoint, rect, normalizedX, normalizedY });
                    
                    wsConnection.send(JSON.stringify({
                        type: 'draw_action',
                        action: 'move',
                        x: normalizedX,
                        y: normalizedY,
                        pressure: pressure,
                        tool: tool,
                        userId: currentUserId,
                        timestamp: now
                    }));
                    setLastCanvasUpdate(now);
                }
            }
        }
    };

    const handleImageUpload = event => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onloadend = () => {
            setImgSrc(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width/2, canvas.height/2);
        
        // Send canvas_clear event through WebSocket if connected
        if (wsConnection && remoteConnected) {
            wsConnection.send(JSON.stringify({
                type: 'canvas_clear'
            }));
        }
    };

    const downloadCanvas = () => {
        const canvas = canvasRef.current;
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = sessionName ? `${sessionName}.png` : 'whiteboard-drawing.png';
        link.click();
    };
    
    const addShape = (type) => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        const centerX = canvas.width / 4; // Divide by 4 because of the 2x scale
        const centerY = canvas.height / 4;
        
        context.globalCompositeOperation = 'source-over';
        context.strokeStyle = lineColor;
        context.fillStyle = lineColor;
        context.lineWidth = lineWidth;
        
        context.beginPath();
        
        if (type === 'circle') {
            context.arc(centerX, centerY, 50, 0, 2 * Math.PI);
        } else if (type === 'rectangle') {
            context.rect(centerX - 50, centerY - 50, 100, 100);
        } else if (type === 'triangle') {
            context.moveTo(centerX, centerY - 50);
            context.lineTo(centerX - 50, centerY + 50);
            context.lineTo(centerX + 50, centerY + 50);
            context.closePath();
        }
        
        context.stroke();
    };

    // Save session functionality
    const saveSession = () => {
        if (!sessionName.trim()) {
            alert('Please enter a session name');
            return;
        }

        const canvas = canvasRef.current;
        const sessionData = {
            id: Date.now().toString(),
            name: sessionName,
            image: canvas.toDataURL('image/png'),
            savedAt: new Date().toLocaleString(),
            settings: {
                lineColor,
                lineWidth,
                imgSrc,
                imgWidth,
                imgHeight,
                imgX,
                imgY
            }
        };

        try {
            // Add to state
            const updatedSessions = [...savedSessions, sessionData];
            setSavedSessions(updatedSessions);
            
            // Save to localStorage
            localStorage.setItem('whiteboard-sessions', JSON.stringify(updatedSessions));
            
            alert(`Session "${sessionName}" saved successfully!`);
        } catch (error) {
            console.error('Error saving session:', error);
            alert('Failed to save session. Please try again.');
        }
    };

    // Load session functionality
    const loadSession = (session) => {
        try {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            // First clear the canvas
            context.clearRect(0, 0, canvas.width/2, canvas.height/2);
            
            // Restore settings
            setLineColor(session.settings.lineColor);
            setLineWidth(session.settings.lineWidth);
            setImgSrc(session.settings.imgSrc);
            setImgWidth(session.settings.imgWidth);
            setImgHeight(session.settings.imgHeight);
            setImgX(session.settings.imgX);
            setImgY(session.settings.imgY);
            setSessionName(session.name);
            
            // Load image data
            const img = new Image();
            img.onload = () => {
                context.drawImage(img, 0, 0, canvas.width/2, canvas.height/2);
            };
            img.src = session.image;
        } catch (error) {
            console.error('Error loading session:', error);
            alert('Failed to load session. Please try again.');
        }
    };

    // Delete session functionality
    const deleteSession = (id) => {
        try {
            const updatedSessions = savedSessions.filter(session => session.id !== id);
            setSavedSessions(updatedSessions);
            localStorage.setItem('whiteboard-sessions', JSON.stringify(updatedSessions));
            alert('Session deleted successfully!');
        } catch (error) {
            console.error('Error deleting session:', error);
            alert('Failed to delete session. Please try again.');
        }
    };

    // Export session as other file types
    const exportSession = (fileType) => {
        const canvas = canvasRef.current;
        let dataUrl;
        
        switch(fileType) {
            case 'png':
                dataUrl = canvas.toDataURL('image/png');
                break;
            case 'jpg':
                dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                break;
            case 'svg':
                alert('SVG export is not supported in this version');
                return;
            case 'pdf':
                alert('PDF export is not supported in this version');
                return;
            case 'session':
                // Save as a Whiteboard session file (JSON)
                const sessionData = {
                    name: sessionName || 'Untitled Session',
                    image: canvas.toDataURL('image/png'),
                    savedAt: new Date().toLocaleString(),
                    settings: {
                        lineColor,
                        lineWidth,
                        imgSrc,
                        imgWidth,
                        imgHeight,
                        imgX,
                        imgY
                    }
                };
                
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(sessionData));
                const downloadLink = document.createElement("a");
                downloadLink.href = dataStr;
                downloadLink.download = `${sessionName || 'whiteboard-session'}.wbs`;
                downloadLink.click();
                return;
            default:
                dataUrl = canvas.toDataURL('image/png');
        }
        
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${sessionName || 'whiteboard-drawing'}.${fileType}`;
        link.click();
    };

    // Import session file
    const importSession = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const sessionData = JSON.parse(e.target.result);
                
                // Validate that this is a proper session file
                if (!sessionData.image || !sessionData.settings) {
                    throw new Error('Invalid session file');
                }
                
                // Load the session
                loadSession({
                    id: Date.now().toString(),
                    name: sessionData.name,
                    image: sessionData.image,
                    savedAt: sessionData.savedAt,
                    settings: sessionData.settings
                });
                
                alert(`Session "${sessionData.name}" imported successfully!`);
            } catch (error) {
                console.error('Error importing session:', error);
                alert('Failed to import session. Invalid file format.');
            }
        };
        reader.readAsText(file);
    };

    const predefinedColors = [
        '#1a73e8', // Blue
        '#d93025', // Red
        '#188038', // Green
        '#f9ab00', // Yellow
        '#5f6368', // Grey
        '#000000', // Black
        '#9c27b0', // Purple
        '#ff5722', // Orange
    ];

    // Function to generate a unique session ID for remote drawing
    const generateRemoteSession = () => {
        // Generate a unique session ID (combination of timestamp and random string)
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(2, 10);
        const sessionId = `${timestamp}-${randomString}`;
        
        setRemoteSessionId(sessionId);
        
        // Save current canvas state
        const canvasState = saveCanvasState();
        
        // Store the session in localStorage with the current canvas state
        try {
            const sessions = JSON.parse(localStorage.getItem('whiteboard-sessions') || '[]');
            const sessionData = {
                id: sessionId,
                name: sessionName || `Session ${sessions.length + 1}`,
                date: new Date().toISOString(),
                thumbnail: canvasRef.current.toDataURL('image/png', 0.1),
                canvasState
            };
            
            localStorage.setItem(`whiteboard-session-${sessionId}`, JSON.stringify(sessionData));
            
            // Set up WebSocket connection
            initializeWebSocket(sessionId);
            
            // Generate QR code URL
            const shareableUrl = `${window.location.origin}/join-whiteboard/${sessionId}`;
            setShareUrl(shareableUrl);
            setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareableUrl)}`);
            
            setShowRemoteControls(true);
            setShowShareOptions(true);
            
            return sessionId;
        } catch (error) {
            console.error('Error generating remote session:', error);
            return null;
        }
    };

    // Function to share the current session with others
    const shareSession = () => {
        setIsSharing(true);
        
        // If we don't have a session ID yet, generate one
        if (!remoteSessionId) {
            const sessionId = generateRemoteSession();
            if (!sessionId) {
                setIsSharing(false);
                return;
            }
        } else {
            // Already have a session, just show share options
            setShowShareOptions(true);
        }
    };
    
    // Copy share link to clipboard
    const copyShareLink = () => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                // Show a temporary success message
                const copyButton = document.querySelector('.copy-link-btn');
                if (copyButton) {
                    const originalText = copyButton.textContent;
                    copyButton.textContent = 'Copied!';
                    setTimeout(() => {
                        copyButton.textContent = originalText;
                    }, 2000);
                }
            })
            .catch(err => {
                console.error('Failed to copy link: ', err);
            });
    };

    // Initialize WebSocket connection
    const initializeWebSocket = (sessionId) => {
        // Close any existing connection
        if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
            console.log('Closing existing WebSocket connection');
            wsConnection.close();
        }
        
        // Generate or use provided user ID
        let userId;
        if (finalJoinMode) {
            userId = `guest_${Math.random().toString(36).substring(2, 10)}`;
        } else {
            userId = localStorage.getItem('whiteboard_user_id') || `user_${Math.random().toString(36).substring(2, 10)}`;
            localStorage.setItem('whiteboard_user_id', userId);
        }
        
        const wsURL = `wss://api.andrewslearning.com/wschat/whiteboard/${sessionId}/${userId}/`;
        console.log('Connecting to WebSocket URL:', wsURL);
        
        const ws = new WebSocket(wsURL);
        
        // Add connection timeout
        const connectionTimeout = setTimeout(() => {
            if (ws.readyState !== WebSocket.OPEN) {
                console.log('WebSocket connection timed out');
                setRemoteConnected(false);
                ws.close();
            }
        }, 10000);
        
        ws.onopen = () => {
            console.log('WebSocket connection opened successfully');
            clearTimeout(connectionTimeout);
            setRemoteConnected(true);
            setShowRemoteControls(true);
            
            // Send client info with guest status
            ws.send(JSON.stringify({
                type: 'client_info',
                userAgent: navigator.userAgent,
                screenWidth: window.innerWidth,
                screenHeight: window.innerHeight,
                isHost: isHost,
                username: finalUserName || sessionName || (isHost ? 'Host' : 'Guest'),
                isGuest: finalJoinMode
            }));
            
            // If guest, request current canvas state
            if (finalJoinMode && !isHost) {
                ws.send(JSON.stringify({
                    type: 'request_canvas_state'
                }));
            }
            
            // Create and send initial canvas state (only if host)
            if (isHost) {
                const initialState = saveCanvasState();
                if (initialState) {
                    ws.send(JSON.stringify({
                        type: 'canvas_state_update',
                        state: initialState,
                        initial: true
                    }));
                }
            }
            
            // Set up ping interval to keep connection alive
            const pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    console.log('Sending ping to keep connection alive');
                    ws.send(JSON.stringify({ type: 'ping' }));
                } else {
                    clearInterval(pingInterval);
                }
            }, 30000); // Send ping every 30 seconds
            
            // Store the interval to clear it later
            ws.pingInterval = pingInterval;
        };
        
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('WebSocket message received:', data);
                
                if (data.type === 'pong') {
                    console.log('Received pong from server');
                    return;
                }
                
                if (data.type === 'connection_established') {
                    console.log('Connection established', data);
                    
                    // Check if this client is the host
                    if (data.is_host) {
                        console.log('This client is the host of the whiteboard session');
                        setIsHost(true);
                    } else {
                        setIsHost(false);
                    }
                    
                    // Update connection state
                    setRemoteConnected(true);
                }
                else if (data.type === 'draw_action') {
                    // Handle drawing actions from other users
                    const actionType = data.action;
                    
                    if (actionType === 'start') {
                        handleRemoteDrawStart(data);
                    } else if (actionType === 'move') {
                        handleRemoteDrawMove(data);
                    } else if (actionType === 'end') {
                        handleRemoteDrawEnd(data);
                    }
                }
                // Handle new streaming drawing messages
                else if (data.type === 'draw_start') {
                    handleRemoteDrawStart(data);
                }
                else if (data.type === 'draw_move') {
                    handleRemoteDrawMove(data);
                }
                else if (data.type === 'draw_end') {
                    handleRemoteDrawEnd(data);
                }
                else if (data.type === 'canvas_state') {
                    // Handle canvas state updates - only if not currently drawing locally
                    if (!localDrawing && data.data) {
                        setIsProcessingRemoteUpdate(true);
                        loadCanvasState(data.data);
                        setLastCanvasUpdate(Date.now());
                        setIsProcessingRemoteUpdate(false);
                    }
                }
                else if (data.type === 'command_draw') {
                    // Handle drawing commands
                    console.log('Received command_draw message:', data);
                    executeDrawCommand(data);
                }
                else if (data.type === 'user_joined') {
                    console.log(`User joined: ${data.username || 'Guest'} (${data.user_id})`);
                    
                    // Update the connected users list
                    setConnectedUsers(prevUsers => {
                        // Make sure we don't add duplicates
                        if (!prevUsers.some(user => user.id === data.user_id)) {
                            return [...prevUsers, {
                                id: data.user_id,
                                name: data.username || `Guest ${data.user_id.slice(0, 4)}`,
                                isDevice: data.is_device || false,
                                deviceType: data.device_type || 'unknown',
                                joinedAt: new Date().toISOString()
                            }];
                        }
                        return prevUsers;
                    });
                    
                    // If we're the host, send the current canvas state to the new user
                    if (isHost) {
                        const currentState = saveCanvasState();
                        if (currentState) {
                            ws.send(JSON.stringify({
                                type: 'canvas_state_update',
                                state: currentState,
                                targetUser: data.user_id
                            }));
                        }
                    }
                }
                else if (data.type === 'user_left') {
                    console.log(`User left: ${data.username || 'Guest'} (${data.user_id})`);
                    
                    // Remove user from the connected users list
                    setConnectedUsers(prevUsers => 
                        prevUsers.filter(user => user.id !== data.user_id)
                    );
                    
                    // If the host left and we've been here the longest, we become the new host
                    if (data.was_host && data.new_host_id === 'your_user_id') {
                        setIsHost(true);
                        console.log('You are now the host of this session');
                    }
                }
                else if (data.type === 'host_changed') {
                    console.log(`Host changed to: ${data.new_host_username || 'Guest'} (${data.new_host_id})`);
                    
                    // Update our host status
                    setIsHost(data.new_host_id === 'your_user_id');
                    
                    // Update the user in our connected users list
                    setConnectedUsers(prevUsers => 
                        prevUsers.map(user => 
                            user.id === data.new_host_id 
                                ? {...user, isHost: true} 
                                : {...user, isHost: false}
                        )
                    );
                }
                else if (data.type === 'connected_users') {
                    // Server is sending the complete list of connected users
                    console.log('Received connected users list:', data.users);
                    
                    if (data.users && Array.isArray(data.users)) {
                        setConnectedUsers(data.users.map(user => ({
                            id: user.id,
                            name: user.username || `Guest ${user.id.slice(0, 4)}`,
                            isDevice: user.is_device || false,
                            deviceType: user.device_type || 'unknown',
                            isHost: user.is_host || false,
                            joinedAt: user.joined_at || new Date().toISOString()
                        })));
                    }
                }
                else if (data.type === 'canvas_clear') {
                    // Clear the canvas when the host clears it
                    clearCanvas();
                }
                else if (data.type === 'tool_change') {
                    // Another user changed their tool settings
                    if (data.tool) {
                        // We don't set our tool to match, but we could if we wanted
                        console.log(`User ${data.user_id} changed tool to ${data.tool}`);
                    }
                    if (data.color) {
                        // Same with color - we track it for their drawings but don't change our tool
                        console.log(`User ${data.user_id} changed color to ${data.color}`);
                    }
                }
                else if (data.type === 'error') {
                    console.error('WebSocket error:', data.message);
                    
                    // Show an error message to the user
                    alert(`Error: ${data.message}`);
                }
                else if (data.type === 'canvas_analysis') {
                    // Handle canvas analysis results from smart_command
                    console.log('Received canvas analysis:', data.analysis);
                    
                    // Add to chat messages
                    const botResponse = { 
                        sender: 'bot', 
                        text: data.analysis 
                    };
                    setChatMessages(prevMessages => [...prevMessages, botResponse]);
                }
            } catch (error) {
                console.error('Error processing WebSocket message:', error);
            }
        };
        
        ws.onclose = (event) => {
            console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
            clearTimeout(connectionTimeout);
            
            if (ws.pingInterval) {
                clearInterval(ws.pingInterval);
            }
            
            setRemoteConnected(false);
            
            // Only hide controls if we intentionally closed the connection
            if (event.code === 1000) {
                setShowRemoteControls(false);
                setShowShareOptions(false);
            } else {
                // Otherwise, try to reconnect after a delay
                console.log('Unexpected connection close. Attempting to reconnect in 5 seconds...');
                setTimeout(() => {
                    console.log('Attempting to reconnect...');
                    initializeWebSocket(sessionId);
                }, 5000);
            }
        };
        
        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            clearTimeout(connectionTimeout);
            setRemoteConnected(false);
        };
        
        setWsConnection(ws);
    };
    
    // Load canvas state from server
    const loadCanvasState = (stateData) => {
        if (!stateData || !stateData.image) return;
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // Clear current canvas
        context.clearRect(0, 0, canvas.width/2, canvas.height/2);
        
        // Load the image
        const img = new Image();
        img.onload = () => {
            context.drawImage(img, 0, 0, canvas.width/2, canvas.height/2);
        };
        img.src = stateData.image;
        
        // Load settings if provided
        if (stateData.settings) {
            if (stateData.settings.lineColor) setLineColor(stateData.settings.lineColor);
            if (stateData.settings.lineWidth) setLineWidth(stateData.settings.lineWidth);
        }
    };
    
    // Save current canvas state to server or for local use
    const saveCanvasState = () => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        
        // Create a state object with canvas image and settings
        const stateData = {
            image: canvas.toDataURL('image/png'),
            timestamp: Date.now(),
            settings: {
                lineColor,
                lineWidth,
                tool
            }
        };
        
        // If connected to WebSocket, send the update
        if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
            wsConnection.send(JSON.stringify({
                type: 'canvas_state_update',
                state: stateData
            }));
        }
        
        // Return the state data for other uses
        return stateData;
    };
    
    // Handle remote drawing events from WebSocket
    const handleRemoteDrawStart = (data) => {
        // Don't process remote drawing if we're currently drawing locally
        if (localDrawing) {
            console.log('Ignoring remote draw start - local drawing in progress');
            return;
        }
        
        // Don't interfere with our own drawing
        if (data.userId === currentUserId) {
            return;
        }
        
        setIsProcessingRemoteUpdate(true);
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // Store the current context state
        const originalCompositeOperation = context.globalCompositeOperation;
        const originalStrokeStyle = context.strokeStyle;
        const originalLineWidth = context.lineWidth;
        
        // Use the remote device's tool settings
        context.globalCompositeOperation = data.isEraser ? 'destination-out' : 'source-over';
        context.strokeStyle = data.color || lineColor;
        context.lineWidth = data.width || lineWidth;
        
        // Start drawing path for remote user
        const rect = canvas.getBoundingClientRect();
        // Convert normalized coordinates (0-1) back to actual display coordinates
        const actualX = data.x * rect.width;
        const actualY = data.y * rect.height;
        
        console.log('Receiving start:', { data, rect, actualX, actualY });
        
        context.beginPath();
        context.moveTo(actualX, actualY);
        
        // Store remote drawing session
        const remoteSession = {
            userId: data.userId,
            lastPoint: { 
                x: actualX, 
                y: actualY,
                pressure: data.pressure || 1 
            },
            isDrawing: true,
            tool: data.tool,
            color: data.color,
            width: data.width,
            isEraser: data.isEraser
        };
        
        setRemoteDrawingSessions(prev => new Map(prev.set(data.userId, remoteSession)));
        
        // Restore original context state
        context.globalCompositeOperation = originalCompositeOperation;
        context.strokeStyle = originalStrokeStyle;
        context.lineWidth = originalLineWidth;
        
        setIsProcessingRemoteUpdate(false);
    };
    
    const handleRemoteDrawMove = (data) => {
        // Don't process remote drawing if we're currently drawing locally
        if (localDrawing) {
            console.log('Ignoring remote draw move - local drawing in progress');
            return;
        }
        
        // Don't interfere with our own drawing
        if (data.userId === currentUserId) {
            return;
        }
        
        const remoteSession = remoteDrawingSessions.get(data.userId);
        if (!remoteSession || !remoteSession.isDrawing || !remoteSession.lastPoint) {
            return;
        }
        
        setIsProcessingRemoteUpdate(true);
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // Store the current context state
        const originalCompositeOperation = context.globalCompositeOperation;
        const originalStrokeStyle = context.strokeStyle;
        const originalLineWidth = context.lineWidth;
        
        // Apply remote user's settings
        context.globalCompositeOperation = remoteSession.isEraser ? 'destination-out' : 'source-over';
        context.strokeStyle = remoteSession.color || lineColor;
        context.lineWidth = remoteSession.width || lineWidth;
        
        // Convert percentage coordinates to actual canvas coordinates
        const rect = canvas.getBoundingClientRect();
        // Convert normalized coordinates (0-1) back to actual display coordinates
        const actualX = data.x * rect.width;
        const actualY = data.y * rect.height;
        
        const currentPoint = {
            x: actualX,
            y: actualY,
            pressure: data.pressure || 1
        };
        
        console.log('Receiving move:', { data, rect, actualX, actualY, lastPoint: remoteSession.lastPoint });
        
        // Draw smooth line from last point to current point
        const midPoint = drawSmoothLine(context, remoteSession.lastPoint, currentPoint, currentPoint.pressure);
        
        // Update remote session
        const updatedSession = {
            ...remoteSession,
            lastPoint: midPoint
        };
        setRemoteDrawingSessions(prev => new Map(prev.set(data.userId, updatedSession)));
        
        // Restore original context state
        context.globalCompositeOperation = originalCompositeOperation;
        context.strokeStyle = originalStrokeStyle;
        context.lineWidth = originalLineWidth;
        
        setIsProcessingRemoteUpdate(false);
    };
    
    const handleRemoteDrawEnd = (data) => {
        // Don't interfere with our own drawing
        if (data.userId === currentUserId) {
            return;
        }
        
        const remoteSession = remoteDrawingSessions.get(data.userId);
        if (!remoteSession) {
            return;
        }
        
        setIsProcessingRemoteUpdate(true);
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // End the remote drawing session
        context.closePath();
        
        // Remove the remote session
        setRemoteDrawingSessions(prev => {
            const newMap = new Map(prev);
            newMap.delete(data.userId);
            return newMap;
        });
        
        setIsProcessingRemoteUpdate(false);
    };
    
    const handleConnectionStatus = (data) => {
        if (data.connected) {
            setRemoteConnected(true);
            // Show notification that a device has connected
        } else {
            // Show notification that a device has disconnected
        }
    };
    
    const handleRemoteSettingsChange = (settings) => {
        // Update local settings based on remote device settings
        if (settings.tool) setTool(settings.tool);
        if (settings.color) setLineColor(settings.color);
        if (settings.lineWidth) setLineWidth(settings.lineWidth);
    };
    
    // Clean up WebSocket on component unmount
    useEffect(() => {
        return () => {
            if (wsConnection) {
                console.log('Cleaning up WebSocket connection on component unmount');
                // Use code 1000 for normal closure
                if (wsConnection.readyState === WebSocket.OPEN) {
                    wsConnection.close(1000, 'Component unmounted');
                }
                
                // Clear any ping intervals
                if (wsConnection.pingInterval) {
                    clearInterval(wsConnection.pingInterval);
                }

            }
        };
    }, [wsConnection]);

    // Function to check if WebSocket is connected and ready
    const isWebSocketReady = () => {
        return wsConnection && wsConnection.readyState === WebSocket.OPEN;
    };
    
    // Function to ensure WebSocket is connected before sending commands
    const ensureWebSocketConnection = (callback) => {
        if (isWebSocketReady()) {
            // If already connected, execute callback immediately
            callback();
            return true;
        } else {
            // Try to connect first
            const sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
            console.log(`Creating new WebSocket connection with ID: ${sessionId}`);
            
            // Store the ID and begin connection
            setRemoteSessionId(sessionId);
            
            // Create a promise to handle the connection
            const connectionPromise = new Promise((resolve, reject) => {
                try {
                    const ws = new WebSocket(`wss://api.andrewslearning.com/wschat/whiteboard/${sessionId}/`);
                    
                    ws.onopen = () => {
                        console.log("WebSocket successfully connected");
                        setWsConnection(ws);
                        setRemoteConnected(true);
                        resolve(ws);
                    };
                    
                    ws.onerror = (error) => {
                        console.error("WebSocket connection error:", error);
                        reject(error);
                    };
                    
                    // Set a timeout for the connection
                    setTimeout(() => {
                        if (ws.readyState !== WebSocket.OPEN) {
                            reject(new Error("WebSocket connection timed out"));
                        }
                    }, 5000);
                    
                } catch (error) {
                    console.error("Error creating WebSocket:", error);
                    reject(error);
                }
            });
            
            // Try to perform the callback when connected
            connectionPromise
                .then(ws => {
                    console.log("Connection established, executing callback");
                    callback();
                    return true;
                })
                .catch(error => {
                    console.error("Failed to establish connection:", error);
                    
                    // Execute locally anyway
                    callback(true);
                    return false;
                });
            
            return false;
        }
    };
    
    // Modified function to send drawing commands through WebSocket
    const sendDrawingCommand = (commandData) => {
        console.log("Preparing to send drawing command:", commandData);
        
        // Always execute locally for immediate feedback
        executeDrawCommand(commandData);
        
        // Try to send via WebSocket
        if (isWebSocketReady()) {
            try {
                console.log("Sending command via WebSocket");
                wsConnection.send(JSON.stringify(commandData));
                return true;
            } catch (error) {
                console.error("Error sending command via WebSocket:", error);
                return false;
            }
        } else {
            console.warn("WebSocket not ready, command executed locally only");
            return false;
        }
    };

    // Check if a drawing tool is active
    const isToolActive = () => {
        // Determine if we're in a drawing mode
        // This can be expanded based on your tool implementation
        return true; // By default assume we want to draw
    };
    
    // Apply transform to canvas for zoom and pan
    const applyTransform = (context, scaleValue, offsetValue, width, height) => {
        if (!context) return;
        
        // Save the current state
        context.save();
        
        // Calculate canvas center
        const centerX = width / 2;
        const centerY = height / 2;
        
        // Apply the transform - scale around center and translate
        context.translate(offsetValue.x, offsetValue.y);
        context.translate(centerX, centerY);
        context.scale(scaleValue, scaleValue);
        context.translate(-centerX, -centerY);
        
        // Restore to go back to the transformed state
        context.restore();
    };
    
    // Reset zoom and pan
    const resetView = () => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    };

    // Add chatbot functions
    const toggleChatbot = () => {
        const newState = !showChatbot;
        setShowChatbot(newState);
        
        // If we're opening the chatbot and don't have a WebSocket connection, try to connect
        if (newState && (!wsConnection || wsConnection.readyState !== WebSocket.OPEN)) {
            console.log("Chatbot opened without active WebSocket, attempting to connect...");
            const testSessionId = `chat-${Date.now()}`;
            setRemoteSessionId(testSessionId);
            initializeWebSocket(testSessionId);
            
            // Add a message about connection attempt
            setTimeout(() => {
                const botResponse = { 
                    sender: 'bot', 
                    text: 'Attempting to establish WebSocket connection...' 
                };
                setChatMessages(prevMessages => [...prevMessages, botResponse]);
            }, 500);
        }
    };
    
    const handleChatInputChange = (e) => {
        setChatInput(e.target.value);
    };
    
    const handleChatKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendChatMessage();
        }
    };

    // Parse and process drawing commands
    const processDrawingCommand = (command) => {
        const parts = command.trim().toLowerCase().split(' ');
        
        console.log('Processing command:', command);
        console.log('WebSocket connection:', wsConnection ? 'exists' : 'null');
        console.log('WebSocket state:', wsConnection ? 
            (wsConnection.readyState === WebSocket.CONNECTING ? 'CONNECTING' : 
             wsConnection.readyState === WebSocket.OPEN ? 'OPEN' : 
             wsConnection.readyState === WebSocket.CLOSING ? 'CLOSING' : 'CLOSED') : 'no connection');
        
        if (parts.length < 2) return null;
        
        const action = parts[0];
        
        // Command structure: draw line x1 y1 x2 y2 [color] [width]
        if (action === 'draw' && parts[1] === 'line' && parts.length >= 6) {
            const x1 = parseInt(parts[2]);
            const y1 = parseInt(parts[3]);
            const x2 = parseInt(parts[4]);
            const y2 = parseInt(parts[5]);
            
            // Optional parameters
            const color = parts.length >= 7 ? parts[6] : lineColor;
            const width = parts.length >= 8 ? parseInt(parts[7]) : lineWidth;
            
            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
                // Create command data
                const commandData = {
                    type: 'command_draw',
                    action: 'line',
                    x1: x1,
                    y1: y1,
                    x2: x2,
                    y2: y2,
                    color: color,
                    width: width
                };
                
                // Send command via WebSocket
                sendDrawingCommand(commandData);
                
                return `Drawing line from (${x1},${y1}) to (${x2},${y2})`;
            }
        }
        // Command structure: draw circle x y radius [color] [width]
        else if (action === 'draw' && parts[1] === 'circle' && parts.length >= 5) {
            const x = parseInt(parts[2]);
            const y = parseInt(parts[3]);
            const radius = parseInt(parts[4]);
            
            // Optional parameters
            const color = parts.length >= 6 ? parts[5] : lineColor;
            const width = parts.length >= 7 ? parseInt(parts[6]) : lineWidth;
            
            if (!isNaN(x) && !isNaN(y) && !isNaN(radius)) {
                // Create command data
                const commandData = {
                    type: 'command_draw',
                    action: 'circle',
                    x: x,
                    y: y,
                    radius: radius,
                    color: color,
                    width: width
                };
                
                // Send command via WebSocket
                sendDrawingCommand(commandData);
                
                return `Drawing circle at (${x},${y}) with radius ${radius}`;
            }
        }
        // Command structure: draw rectangle x y width height [color] [lineWidth]
        else if (action === 'draw' && parts[1] === 'rectangle' && parts.length >= 6) {
            const x = parseInt(parts[2]);
            const y = parseInt(parts[3]);
            const width = parseInt(parts[4]);
            const height = parseInt(parts[5]);
            
            // Optional parameters
            const color = parts.length >= 7 ? parts[6] : lineColor;
            const lineWidthVal = parts.length >= 8 ? parseInt(parts[7]) : lineWidth;
            
            if (!isNaN(x) && !isNaN(y) && !isNaN(width) && !isNaN(height)) {
                // Create command data
                const commandData = {
                    type: 'command_draw',
                    action: 'rectangle',
                    x: x,
                    y: y,
                    width: width,
                    height: height,
                    color: color,
                    lineWidth: lineWidthVal
                };
                
                // Send command via WebSocket
                sendDrawingCommand(commandData);
                
                return `Drawing rectangle at (${x},${y}) with dimensions ${width}x${height}`;
            }
        }
        // Command structure: draw text x y "text" [color] [fontSize]
        else if (action === 'draw' && parts[1] === 'text' && parts.length >= 5) {
            const x = parseInt(parts[2]);
            const y = parseInt(parts[3]);
            
            // Extract the text - could be in quotes or not
            let text = '';
            let startIdx = 4;
            
            if (parts[4].startsWith('"')) {
                // Find the closing quote
                const textParts = [];
                let i = 4;
                
                // Remove opening quote
                textParts.push(parts[i].substring(1));
                
                // If the first part also has the closing quote
                if (parts[4].endsWith('"')) {
                    text = parts[4].substring(1, parts[4].length - 1);
                    startIdx = 5;
                } else {
                    // Look for the part with the closing quote
                    i++;
                    while (i < parts.length && !parts[i].endsWith('"')) {
                        textParts.push(parts[i]);
                        i++;
                    }
                    
                    if (i < parts.length) {
                        // Remove closing quote
                        textParts.push(parts[i].substring(0, parts[i].length - 1));
                        startIdx = i + 1;
                    }
                    
                    text = textParts.join(' ');
                }
            } else {
                // Just use the next part as text
                text = parts[4];
                startIdx = 5;
            }
            
            // Optional parameters
            const color = parts.length >= startIdx + 1 ? parts[startIdx] : lineColor;
            const fontSize = parts.length >= startIdx + 2 ? parseInt(parts[startIdx + 1]) : 20;
            
            if (!isNaN(x) && !isNaN(y) && text) {
                // Create command data
                const commandData = {
                    type: 'command_draw',
                    action: 'text',
                    x: x,
                    y: y,
                    text: text,
                    color: color,
                    fontSize: fontSize
                };
                
                // Send command via WebSocket
                sendDrawingCommand(commandData);
                
                return `Drawing text "${text}" at (${x},${y})`;
            }
        }
        // Command structure: clear
        else if (action === 'clear') {
            const commandData = {
                type: 'command_draw',
                action: 'clear'
            };
                
            // Send command via WebSocket
            sendDrawingCommand(commandData);
            
            return 'Clearing the canvas';
        }
        
        return null;
    };
    
    // Execute drawing commands on the canvas
    const executeDrawCommand = (command) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const context = canvas.getContext('2d');
        if (!context) return;
        
        // Set default styles
        context.strokeStyle = command.color || lineColor;
        context.lineWidth = command.width || lineWidth;
        context.globalCompositeOperation = 'source-over';
        
        if (command.action === 'line') {
            // Draw a line
            context.beginPath();
            context.moveTo(command.x1, command.y1);
            context.lineTo(command.x2, command.y2);
            context.stroke();
        }
        else if (command.action === 'circle') {
            // Draw a circle
            context.beginPath();
            context.arc(command.x, command.y, command.radius, 0, 2 * Math.PI);
            context.stroke();
        }
        else if (command.action === 'rectangle') {
            // Draw a rectangle
            context.beginPath();
            context.rect(command.x, command.y, command.width, command.height);
            context.stroke();
        }
        else if (command.action === 'text') {
            // Draw text
            context.font = `${command.fontSize || 20}px ${fontFamily}`;
            context.fillStyle = command.color || lineColor;
            context.fillText(command.text, command.x, command.y);
        }
        else if (command.action === 'clear') {
            // Clear the canvas
            context.clearRect(0, 0, canvas.width/2, canvas.height/2);
        }
    };
    
    const sendChatMessage = () => {
        if (chatInput.trim() === '') return;
        
        // Add user message
        const newUserMessage = { sender: 'user', text: chatInput };
        setChatMessages(prevMessages => [...prevMessages, newUserMessage]);
        
        // Check if the message is a drawing command
        const commandResult = processDrawingCommand(chatInput);
        
        // Clear input
        setChatInput('');
        
        // If it was a valid command, add a confirmation message
        if (commandResult) {
            setTimeout(() => {
                const botResponse = { 
                    sender: 'bot', 
                    text: `✓ ${commandResult}` 
                };
                setChatMessages(prevMessages => [...prevMessages, botResponse]);
            }, 300);
        } else {
            // Regular chat response
            setTimeout(() => {
                const botResponse = { 
                    sender: 'bot', 
                    text: `I received your message: "${chatInput}". To draw, try commands like:\n- draw line 50 50 200 200\n- draw circle 150 150 50\n- draw rectangle 100 100 200 150\n- draw text 100 100 "Hello world"\n- clear` 
                };
                setChatMessages(prevMessages => [...prevMessages, botResponse]);
            }, 1000);
        }
    };

    // Inside the WhiteBoard component, add a new function to test WebSocket connection
    const testWebSocketConnection = () => {
        // Generate a test session ID
        const testSessionId = `test-${Date.now()}`;
        console.log(`Testing WebSocket connection with session ID: ${testSessionId}`);
        
        // Store the session ID
        setRemoteSessionId(testSessionId);
        
        // Try to connect
        initializeWebSocket(testSessionId);
    };

    // Add a simple WebSocket test to a known working endpoint
    const testSimpleWebSocket = () => {
        console.log('Testing basic WebSocket functionality with echo.websocket.org');
        
        try {
            // Connect to a public WebSocket echo server to test basic functionality
            const echoWs = new WebSocket('wss://echo.websocket.org');
            
            echoWs.onopen = () => {
                console.log('Echo WebSocket connected successfully!');
                // Send a test message
                echoWs.send('Hello WebSocket Echo Test');
            };
            
            echoWs.onmessage = (event) => {
                console.log('Echo WebSocket message received:', event.data);
                // Close the connection after receiving the echo
                echoWs.close();
                alert('WebSocket test successful! Check console for details.');
            };
            
            echoWs.onerror = (error) => {
                console.error('Echo WebSocket error:', error);
                alert('WebSocket test failed. Browser might be blocking WebSockets or network issue.');
            };
            
            echoWs.onclose = () => {
                console.log('Echo WebSocket connection closed');
            };
        } catch (error) {
            console.error('Failed to create Echo WebSocket:', error);
            alert('Failed to create WebSocket connection: ' + error.message);
        }
    };

    return (
        <div className="whiteboard-container">
            {/* Chatbot Component */}
            <div className={`chatbot-container ${!showChatbot ? 'closed' : ''}`}>
                <div className="chatbot-toggle" onClick={toggleChatbot}>
                    {showChatbot ? 'Close Assistant' : 'AI Drawing Assistant'}
                </div>
                
                {showChatbot && (
                    <div className="chatbot-panel">
                        <div className="chatbot-header">
                            <h3>AI Drawing Assistant</h3>
                        </div>
                        
                        <div className="chatbot-messages">
                            {chatMessages.map((msg, index) => (
                                <div key={index} className={`chat-message ${msg.sender}`}>
                                    <div className="message-content">{msg.text}</div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="chatbot-input">
                            <input 
                                type="text" 
                                placeholder="Ask about drawing techniques..." 
                                value={chatInput}
                                onChange={handleChatInputChange}
                                onKeyPress={handleChatKeyPress}
                            />
                            <button onClick={sendChatMessage}>Send</button>
                            <button 
                                onClick={() => {
                                    console.log("Testing WebSocket with direct command");
                                    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
                                        const testCommand = {
                                            type: 'command_draw',
                                            action: 'circle',
                                            x: 200,
                                            y: 200,
                                            radius: 50,
                                            color: '#ff0000',
                                            width: 2
                                        };
                                        sendDrawingCommand(testCommand);
                                        console.log("Test command sent:", testCommand);
                                        
                                        // Add feedback message
                                        const botResponse = { 
                                            sender: 'bot', 
                                            text: '✓ Test circle sent to WebSocket' 
                                        };
                                        setChatMessages(prevMessages => [...prevMessages, botResponse]);
                                    } else {
                                        console.error("WebSocket not connected for test!");
                                        const botResponse = { 
                                            sender: 'bot', 
                                            text: '❌ WebSocket not connected. Connecting now...' 
                                        };
                                        setChatMessages(prevMessages => [...prevMessages, botResponse]);
                                        
                                        // Try to connect automatically
                                        const testSessionId = `test-${Date.now()}`;
                                        setRemoteSessionId(testSessionId);
                                        initializeWebSocket(testSessionId);
                                    }
                                }}
                                className="test-draw-btn"
                            >
                                Test WS Draw
                            </button>
                            <button 
                                onClick={() => {
                                    if (chatInput.trim() === '') {
                                        const botResponse = { 
                                            sender: 'bot', 
                                            text: '❌ Please type a command first' 
                                        };
                                        setChatMessages(prevMessages => [...prevMessages, botResponse]);
                                        return;
                                    }
                                    
                                    // Add user message
                                    const newUserMessage = { sender: 'user', text: chatInput };
                                    setChatMessages(prevMessages => [...prevMessages, newUserMessage]);
                                    
                                    // Send as smart command
                                    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
                                        const smartCommand = {
                                            type: "smart_command",
                                            message: chatInput
                                        };
                                        wsConnection.send(JSON.stringify(smartCommand));
                                        console.log("Smart command sent from chat:", smartCommand);
                                        
                                        // Feedback
                                        const botResponse = { 
                                            sender: 'bot', 
                                            text: '🧠 Processing with LLM...' 
                                        };
                                        setChatMessages(prevMessages => [...prevMessages, botResponse]);
                                    } else {
                                        console.error("WebSocket not connected!");
                                        const botResponse = { 
                                            sender: 'bot', 
                                            text: '❌ WebSocket not connected. Connecting now...' 
                                        };
                                        setChatMessages(prevMessages => [...prevMessages, botResponse]);
                                        
                                        // Try to connect
                                        const testSessionId = `smart-${Date.now()}`;
                                        setRemoteSessionId(testSessionId);
                                        initializeWebSocket(testSessionId);
                                    }
                                    
                                    // Clear input
                                    setChatInput('');
                                }}
                                className="smart-command-btn"
                                style={{ backgroundColor: '#9c27b0' }}
                            >
                                Smart Process
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            <div className={`toolbar ${showControls ? 'expanded' : 'collapsed'}`}>
                <div className="toolbar-toggle" onClick={() => setShowControls(!showControls)}>
                    {showControls ? '◀' : '▶'}
                </div>
                
                {/* Add test button */}
                <div className="toolbar-section">
                    <h3>Debug</h3>
                    <button 
                        className="action-btn"
                        onClick={testWebSocketConnection}
                        style={{ marginBottom: '10px', backgroundColor: '#ff5722' }}
                    >
                        Test WebSocket Connection
                    </button>
                    <button 
                        className="action-btn"
                        onClick={testSimpleWebSocket}
                        style={{ marginBottom: '10px', backgroundColor: '#4CAF50' }}
                    >
                        Test Basic WebSocket
                    </button>
                </div>
                
                {/* Join Session Section */}
                <div className="toolbar-section">
                    <h3>Join Session</h3>
                    <div className="join-session-controls">
                        <input
                            type="text"
                            placeholder="Enter Session ID"
                            value={remoteSessionId}
                            onChange={(e) => setRemoteSessionId(e.target.value)}
                            className="session-input"
                            style={{ 
                                width: '100%', 
                                padding: '8px', 
                                marginBottom: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                            }}
                        />
                        <div className="join-session-buttons">
                            <button 
                                className="action-btn"
                                onClick={() => {
                                    if (!remoteSessionId.trim()) {
                                        alert('Please enter a session ID');
                                        return;
                                    }
                                    console.log(`Joining session: ${remoteSessionId}`);
                                    setIsHost(false);
                                    initializeWebSocket(remoteSessionId);
                                }}
                                style={{ 
                                    marginBottom: '5px', 
                                    backgroundColor: '#2196F3',
                                    width: '100%'
                                }}
                                disabled={remoteConnected}
                            >
                                {remoteConnected ? 'Connected' : 'Join as Guest'}
                            </button>
                            <button 
                                className="action-btn"
                                onClick={() => {
                                    if (!remoteSessionId.trim()) {
                                        const newSessionId = generateRemoteSession();
                                        if (newSessionId) {
                                            console.log(`Created new session: ${newSessionId}`);
                                        }
                                    } else {
                                        console.log(`Creating/hosting session: ${remoteSessionId}`);
                                        setIsHost(true);
                                        initializeWebSocket(remoteSessionId);
                                    }
                                }}
                                style={{ 
                                    backgroundColor: '#4CAF50',
                                    width: '100%'
                                }}
                            >
                                {remoteSessionId.trim() ? 'Host Session' : 'Create New Session'}
                            </button>
                        </div>
                        {remoteConnected && (
                            <div className="connection-status" style={{ 
                                marginTop: '10px', 
                                padding: '8px', 
                                backgroundColor: '#e8f5e8', 
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}>
                                <div><strong>Status:</strong> Connected</div>
                                <div><strong>Role:</strong> {isHost ? 'Host' : 'Guest'}</div>
                                <div><strong>Users:</strong> {connectedUsers.length + 1}</div>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="toolbar-section">
                    <h3>Tools</h3>
                    <div className="tool-buttons">
                        <button 
                            className={`tool-btn ${tool === 'pen' ? 'active' : ''}`} 
                            onClick={() => setTool('pen')}
                            title="Pen Tool"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                        </button>
                        <button 
                            className={`tool-btn ${tool === 'eraser' ? 'active' : ''}`} 
                            onClick={() => setTool('eraser')}
                            title="Eraser Tool"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M15.14 3c-.51 0-1.02.2-1.41.59L2.59 14.73c-.78.77-.78 2.04 0 2.83L5.03 20h7.94l8.44-8.44c.79-.78.79-2.04 0-2.83l-4.84-4.84c-.4-.39-.93-.59-1.43-.59zM5.44 18L3 15.56l9.7-9.7 2.44 2.44-9.7 9.7z"/>
                            </svg>
                        </button>
                        <button 
                            className={`tool-btn ${tool === 'crop' ? 'active' : ''}`} 
                            onClick={() => setTool('crop')}
                            title="Crop Tool"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M17 15h2V7c0-1.1-.9-2-2-2H9v2h8v8zM7 17V1H5v4H1v2h4v10c0 1.1.9 2 2 2h10v4h2v-4h4v-2H7z"/>
                            </svg>
                        </button>
                        <button 
                            className={`tool-btn ${tool === 'resize' ? 'active' : ''}`} 
                            onClick={() => setTool('resize')}
                            title="Resize Tool"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M19 12h-2v3h-3v2h5v-5zM7 9h3V7H5v5h2V9zm14-6H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16.01H3V4.99h18v14.02z"/>
                            </svg>
                        </button>
                        <button 
                            className={`tool-btn ${tool === 'text' ? 'active' : ''}`} 
                            onClick={() => setTool('text')}
                            title="Text Tool"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn" 
                            onClick={clearCanvas}
                            title="Clear Canvas"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4zM6 7v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm8 7v4h-4v-4H8l4-4 4 4h-2z"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn" 
                            onClick={downloadCanvas}
                            title="Download Drawing"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn"
                            onClick={shareSession}
                            title="Share Whiteboard"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn"
                            onClick={resetView}
                            title="Reset View"
                            style={{display: scale !== 1 || offset.x !== 0 || offset.y !== 0 ? 'inline-flex' : 'none'}}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="toolbar-section">
                    <h3>Shapes</h3>
                    <div className="tool-buttons">
                        <button 
                            className="tool-btn" 
                            onClick={() => addShape('circle')}
                            title="Add Circle"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn" 
                            onClick={() => addShape('rectangle')}
                            title="Add Rectangle"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <rect x="3" y="3" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                        </button>
                        <button 
                            className="tool-btn" 
                            onClick={() => addShape('triangle')}
                            title="Add Triangle"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M12 2L2 22h20L12 2z" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="toolbar-section">
                    <h3>Colors</h3>
                    <div className="color-palette">
                        {predefinedColors.map((color, index) => (
                            <div 
                                key={index}
                                className={`color-swatch ${color === lineColor ? 'active' : ''}`}
                                style={{ backgroundColor: color }}
                                onClick={() => setLineColor(color)}
                                title={color}
                            />
                        ))}
                        <input 
                            type="color" 
                            value={lineColor} 
                            onChange={e => setLineColor(e.target.value)} 
                            className="color-picker"
                            title="Custom Color"
                        />
                    </div>
                </div>

                <div className="toolbar-section">
                    <h3>Brush Size</h3>
                    <input 
                        type="range" 
                        min="1" 
                        max="30" 
                        value={lineWidth} 
                        onChange={e => setLineWidth(parseInt(e.target.value))}
                        className="slider"
                    />
                    <div className="brush-preview" style={{width: lineWidth, height: lineWidth, backgroundColor: lineColor}}></div>
                </div>

                {/* Text Formatting Section */}
                <div className={`toolbar-section ${tool === 'text' ? 'active' : ''}`}>
                    <h3>Text Formatting</h3>
                    <div className="text-controls">
                        <div className="font-family-select">
                            <label>Font:</label>
                            <select 
                                value={fontFamily} 
                                onChange={(e) => setFontFamily(e.target.value)}
                                className="font-select"
                            >
                                <option value="Arial">Arial</option>
                                <option value="Helvetica">Helvetica</option>
                                <option value="Times New Roman">Times New Roman</option>
                                <option value="Courier New">Courier New</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Verdana">Verdana</option>
                                <option value="Impact">Impact</option>
                                <option value="Comic Sans MS">Comic Sans MS</option>
                            </select>
                        </div>
                        <div className="font-size-control">
                            <label>Size:</label>
                            <div className="font-size-inputs">
                                <input 
                                    type="range" 
                                    min="10" 
                                    max="72" 
                                    value={fontSize} 
                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                    className="slider"
                                />
                                <input 
                                    type="number" 
                                    min="10" 
                                    max="72" 
                                    value={fontSize} 
                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                    className="font-size-number"
                                />
                            </div>
                        </div>
                        <div className="font-preview" style={{ fontFamily, fontSize: `${fontSize}px`, color: lineColor }}>
                            Sample Text
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Drawing Canvas */}
            <div className="canvas-container">
            <canvas 
                    ref={canvasRef}
                    className="drawing-canvas"
                onMouseDown={startDrawing} 
                onMouseMove={draw}
                    onMouseUp={finishDrawing}
                    onMouseOut={finishDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={finishDrawing}
                    onTouchCancel={finishDrawing}
                />
                
                {/* Text Input Overlay */}
                {isTyping && (
                    <div 
                        className="text-input-container"
                        style={{
                            position: 'absolute',
                            left: `${textPosition.x}px`,
                            top: `${textPosition.y}px`,
                            zIndex: 10
                        }}
                    >
                        <textarea
                            ref={(ref) => setTextInputRef(ref)}
                            value={textInputValue}
                            onChange={handleTextChange}
                            onKeyDown={handleTextKeyDown}
                            onBlur={handleTextBlur}
                            style={{
                                fontFamily,
                                fontSize: `${fontSize}px`,
                                color: lineColor,
                                background: 'transparent',
                                border: '1px dashed #ccc'
                            }}
                            autoFocus
            />
        </div>
                )}
            </div>
            
            {/* Share Panel */}
            {showShareOptions && (
                <div className="share-panel-overlay">
                    <div className="share-panel">
                        <div className="share-panel-header">
                            <h2>Share Whiteboard</h2>
                            <button 
                                className="close-btn"
                                onClick={() => setShowShareOptions(false)}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div className="share-panel-content">
                            <div className="share-section">
                                <h3>Invite Link</h3>
                                <p>Anyone with this link can join and collaborate:</p>
                                
                                <div className="invite-link-container">
                                    <div className="invite-url-display">
                                        <strong>Invite URL:</strong>
                                        <code>{shareUrl}</code>
                                    </div>
                                    <div className="invite-actions">
                                        <button 
                                            className="copy-link-btn"
                                            onClick={copyShareLink}
                                        >
                                            📋 Copy Invite Link
                                        </button>
                                        <button 
                                            className="share-email-btn"
                                            onClick={() => {
                                                const subject = encodeURIComponent('Join my Whiteboard Session');
                                                const body = encodeURIComponent(`Hi! I'd like to invite you to collaborate on my whiteboard.\n\nClick this link to join: ${shareUrl}\n\nSession ID: ${remoteSessionId}`);
                                                window.open(`mailto:?subject=${subject}&body=${body}`);
                                            }}
                                        >
                                            📧 Share via Email
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="share-section qr-section">
                                <h3>Or scan this QR code</h3>
                                <div className="qr-code-container">
                                    {qrCodeUrl && (
                                        <img 
                                            src={qrCodeUrl}
                                            alt="QR code to join whiteboard"
                                            className="qr-code"
                                        />
                                    )}
                                </div>
                            </div>
                            
                            <div className="share-section connection-info">
                                <h3>Connection Information</h3>
                                <div className="info-item">
                                    <strong>Session ID:</strong> 
                                    <span className="session-id">{remoteSessionId}</span>
                                </div>
                                <div className="info-item">
                                    <strong>Status:</strong> 
                                    <span className={`connection-status ${remoteConnected ? 'connected' : 'waiting'}`}>
                                        {remoteConnected ? 'Connected' : 'Waiting for connections...'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <strong>Role:</strong> 
                                    <span className="user-role">
                                        {isHost ? 'Host (can control session)' : 'Guest (view and draw only)'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Connected Users */}
                            {connectedUsers.length > 0 && (
                                <div className="share-section connected-users-list">
                                    <h3>Connected Users ({connectedUsers.length})</h3>
                                    <ul className="users-list">
                                        {connectedUsers.map(user => (
                                            <li key={user.id} className={`user-item ${user.isHost ? 'host' : ''}`}>
                                                <div className="user-icon">
                                                    <i className={`fas fa-${user.isDevice ? 'tablet-alt' : 'user'}`}></i>
                                                </div>
                                                <div className="user-details">
                                                    <span className="user-name">
                                                        {user.name} 
                                                        {user.isHost && <span className="host-badge">Host</span>}
                                                    </span>
                                                    <span className="user-joined">
                                                        Joined {new Date(user.joinedAt).toLocaleTimeString()}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WhiteBoard;
